import React, { useState, useEffect, useMemo } from 'react'
import api from '../../services/api'
import { FormGroup, Input, Button, SearchableSelect } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'
import './Admin.css'
import NdflSettings from './NdflSettings'

const REF_TYPE_DISTRIBUTION = 'тип распределения'

function referenceItemLabel(item) {
  if (!item) return ''
  return String(item.name || item.code || item.id || '').trim()
}

function buildDistributionTypeOptions(referenceList, budgetRulesGrouped, savedValues) {
  const values = new Set()
  const add = (raw) => {
    const value = String(raw || '').trim()
    if (value) values.add(value)
  }
  ;(referenceList || []).forEach((item) => add(referenceItemLabel(item)))
  ;(budgetRulesGrouped || []).forEach((group) => add(group.distribution_type))
  ;(savedValues || []).forEach(add)
  return [...values].sort((a, b) => a.localeCompare(b, 'ru'))
}

const DEFAULT_STAGE_PERCENTS = [
  { type: 'ЭП', distribution_type: 'ЭП', contract_cost_percent: 20, cost_percent: 15 },
  { type: 'ПД', distribution_type: 'ПД', contract_cost_percent: 40, cost_percent: 35 },
  { type: 'РД', distribution_type: 'РД', contract_cost_percent: 40, cost_percent: 50 },
]

function parseNumericParam(value, fallback) {
  if (value === '' || value === null || value === undefined) return fallback
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function SystemParams() {
  const [systemParams, setSystemParams] = useState({
    vat_percent: 5,
    default_price_per_sqm: 0,
    default_price_per_parking: 0,
  })
  const [loadingParams, setLoadingParams] = useState(true)
  const [savingParams, setSavingParams] = useState(false)
  const [paramsMessage, setParamsMessage] = useState({ type: '', text: '' })

  const [defaultStagePercents, setDefaultStagePercents] = useState(DEFAULT_STAGE_PERCENTS)
  const [distributionTypeOptions, setDistributionTypeOptions] = useState([])
  const [loadingStagePercents, setLoadingStagePercents] = useState(false)
  const [savingStagePercents, setSavingStagePercents] = useState(false)
  const [stageMessage, setStageMessage] = useState({ type: '', text: '' })

  const distributionSelectOptions = useMemo(
    () => distributionTypeOptions.map((value) => ({ value, label: value })),
    [distributionTypeOptions],
  )

  useEffect(() => {
    let cancelled = false
    setLoadingParams(true)
    api.get('/settings/system-params')
      .then((res) => {
        if (!cancelled && res.data) {
          setSystemParams({
            vat_percent: res.data.vat_percent ?? 5,
            default_price_per_sqm: res.data.default_price_per_sqm ?? 0,
            default_price_per_parking: res.data.default_price_per_parking ?? 0,
          })
        }
      })
      .catch(() => { if (!cancelled) setParamsMessage({ type: 'error', text: 'Не удалось загрузить параметры системы' }) })
      .finally(() => { if (!cancelled) setLoadingParams(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoadingStagePercents(true)
    Promise.all([
      api.get('/settings/default-stage-percents'),
      api.get('/admin/reference').catch(() => ({ data: { references: {} } })),
      api.get('/admin/budget/budget-rules').catch(() => ({ data: { grouped: [] } })),
    ])
      .then(([percentsRes, referenceRes, rulesRes]) => {
        if (cancelled) return
        const rows = percentsRes.data?.default_stage_percents
        const normalizedRows = Array.isArray(rows) && rows.length === 3
          ? rows.map((row) => ({
            ...row,
            distribution_type: String(row.distribution_type || row.type || '').trim(),
          }))
          : DEFAULT_STAGE_PERCENTS
        setDefaultStagePercents(normalizedRows)
        setDistributionTypeOptions(
          buildDistributionTypeOptions(
            referenceRes.data?.references?.[REF_TYPE_DISTRIBUTION],
            rulesRes.data?.grouped,
            normalizedRows.map((row) => row.distribution_type),
          ),
        )
      })
      .catch(() => { if (!cancelled) setStageMessage({ type: 'error', text: 'Не удалось загрузить проценты стадий' }) })
      .finally(() => { if (!cancelled) setLoadingStagePercents(false) })
    return () => { cancelled = true }
  }, [])

  const handleParamsChange = (field, value) => {
    setSystemParams((prev) => ({ ...prev, [field]: value }))
  }

  const handleSaveParams = async (e) => {
    e.preventDefault()
    setSavingParams(true)
    setParamsMessage({ type: '', text: '' })
    try {
      const payload = {
        vat_percent: parseNumericParam(systemParams.vat_percent, 5),
        default_price_per_sqm: parseNumericParam(systemParams.default_price_per_sqm, 0),
        default_price_per_parking: parseNumericParam(systemParams.default_price_per_parking, 0),
      }
      const putRes = await api.put('/settings/system-params', payload)
      setParamsMessage({ type: 'success', text: 'Параметры системы сохранены' })
      const d = putRes.data
      if (d) {
        setSystemParams((prev) => ({
          ...prev,
          vat_percent: d.vat_percent ?? prev.vat_percent,
          default_price_per_sqm: d.default_price_per_sqm ?? prev.default_price_per_sqm,
          default_price_per_parking: d.default_price_per_parking ?? prev.default_price_per_parking,
        }))
      }
    } catch (err) {
      const text = err.response?.data?.error || translateApiError(err.message) || 'Не удалось сохранить'
      setParamsMessage({ type: 'error', text })
    } finally {
      setSavingParams(false)
    }
  }

  const handleStagePercentChange = (index, field, value) => {
    setDefaultStagePercents((prev) => {
      const next = prev.map((row, i) => {
        if (i !== index) return row
        const updated = { ...row, [field]: value }
        if (field === 'type' && (!row.distribution_type || row.distribution_type === row.type)) {
          updated.distribution_type = value
        }
        return updated
      })
      return next
    })
  }

  const handleSaveStagePercents = async (e) => {
    e.preventDefault()
    setSavingStagePercents(true)
    setStageMessage({ type: '', text: '' })
    const payload = defaultStagePercents.map((row) => ({
      type: (row.type || '').trim(),
      distribution_type: (row.distribution_type || row.type || '').trim(),
      contract_cost_percent: Number(row.contract_cost_percent) || 0,
      cost_percent: Number(row.cost_percent) || 0,
    }))
    if (payload.some((row) => !row.distribution_type)) {
      setSavingStagePercents(false)
      setStageMessage({ type: 'error', text: 'Выберите тип распределения для каждой стадии' })
      return
    }
    try {
      await api.put('/settings/default-stage-percents', payload)
      setStageMessage({ type: 'success', text: 'Проценты стадий сохранены' })
    } catch (err) {
      const text = err.response?.data?.error || translateApiError(err.message) || 'Не удалось сохранить'
      setStageMessage({ type: 'error', text })
    } finally {
      setSavingStagePercents(false)
    }
  }

  return (
    <div className="admin-page">
      <p className="admin-subtitle" style={{ marginBottom: 'var(--spacing-lg)' }}>
        НДС, стандартные цены и проценты стадий по умолчанию
      </p>

      <NdflSettings />

      {/* Параметры системы */}
      <div className="admin-settings-panel">
        <h2>НДС и стандартные цены</h2>
        {loadingParams ? (
          <p>Загрузка...</p>
        ) : (
          <form onSubmit={handleSaveParams}>
            <div className="admin-form admin-form--borderless" style={{ display: 'block' }}>
              <FormGroup label="НДС (%)">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={systemParams.vat_percent}
                  onChange={(e) => handleParamsChange('vat_percent', e.target.value === '' ? '' : e.target.value)}
                />
              </FormGroup>
              <FormGroup label="Стандартная стоимость за м² (для объектов)">
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={systemParams.default_price_per_sqm}
                  onChange={(e) => handleParamsChange('default_price_per_sqm', e.target.value === '' ? '' : e.target.value)}
                />
              </FormGroup>
              <FormGroup label="Стандартная стоимость машиноместа (для объектов)">
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={systemParams.default_price_per_parking}
                  onChange={(e) => handleParamsChange('default_price_per_parking', e.target.value === '' ? '' : e.target.value)}
                />
              </FormGroup>
            </div>
            {paramsMessage.text && (
              <p style={{ color: paramsMessage.type === 'error' ? 'var(--color-error)' : 'var(--color-success)', marginBottom: 'var(--spacing-sm)' }}>
                {paramsMessage.text}
              </p>
            )}
            <Button type="submit" variant="primary" disabled={savingParams}>
              {savingParams ? 'Сохранение...' : 'Сохранить параметры'}
            </Button>
          </form>
        )}
      </div>

      {/* Стадии по умолчанию */}
      <div className="admin-settings-panel admin-settings-panel--wide">
        <h2>Стадии по умолчанию</h2>
        <p className="admin-subtitle" style={{ marginBottom: 'var(--spacing-md)' }}>
          При создании договора с привязанным объектом можно автоматически создать три этапа (ЭП, ПД, РД) с бюджетами. Проценты задают долю от стоимости объекта (с НДС), тип распределения — правила бюджета.
        </p>
        {loadingStagePercents ? (
          <p>Загрузка...</p>
        ) : (
          <form onSubmit={handleSaveStagePercents}>
            <div className="admin-table-frame">
            <div className="table-responsive">
              <table className="ui-table data-table">
                <thead>
                  <tr>
                    <th>Тип стадии</th>
                    <th>Тип распределения</th>
                    <th>Стоимость по договору (% от стоимости объекта)</th>
                    <th>Стоимость (% от стоимости объекта)</th>
                  </tr>
                </thead>
                <tbody>
                  {defaultStagePercents.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <Input
                          type="text"
                          value={row.type ?? ''}
                          onChange={(e) => handleStagePercentChange(index, 'type', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <SearchableSelect
                          value={row.distribution_type ?? row.type ?? ''}
                          options={distributionSelectOptions}
                          placeholder="Выберите тип распределения"
                          onChange={(value) => handleStagePercentChange(index, 'distribution_type', value)}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={row.contract_cost_percent ?? ''}
                          onChange={(e) => handleStagePercentChange(index, 'contract_cost_percent', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={row.cost_percent ?? ''}
                          onChange={(e) => handleStagePercentChange(index, 'cost_percent', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
            {stageMessage.text && (
              <p style={{ color: stageMessage.type === 'error' ? 'var(--color-error)' : 'var(--color-success)', marginTop: 'var(--spacing-sm)' }}>
                {stageMessage.text}
              </p>
            )}
            <Button type="submit" variant="primary" disabled={savingStagePercents} style={{ marginTop: 'var(--spacing-md)' }}>
              {savingStagePercents ? 'Сохранение...' : 'Сохранить стадии'}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}

export default SystemParams
